import { z } from 'zod';
import { getChartStyleOptionsSchema } from './chart-options-schemas';
import deepmerge from 'deepmerge';
import { zodToReadableType } from '@/utils/zod-utils';
import {
	BuildChartContext,
	ExtendedChartWidgetProps,
	updateChartSchema,
} from '../types';
import { createStructuredCompletion } from '@/utils/chat-completion-utils';
import { devDebug } from '@/utils/dev-debug';

type UpdateChartStyleOptionsHelperParams = {
	userPrompt: string;
	chartWidgetProps: ExtendedChartWidgetProps;
};

/**
 * Updates chart style options based on the user's prompt.
 * @param userPrompt - The user's prompt to update the chart style options.
 * @param chartWidgetProps - The chart widget props to update the chart style options for.
 * @returns The updated chart.
 */
export const updateChartStyleOptionsHelper = async ({
	userPrompt,
	chartWidgetProps,
}: UpdateChartStyleOptionsHelperParams): Promise<ExtendedChartWidgetProps> => {
	const { styleOptions: currentStyleOptions, chartType: currentChartType } =
		chartWidgetProps;

	// Get the schema for the effective chart type
	const targetSchema = getChartStyleOptionsSchema(currentChartType);

	const editableFieldsDoc = `
		Editable chart style properties are as follows:
		
		styleOptions: {
		${Object.entries(targetSchema.shape)
			.map(([key, value]) => `- ${key}: ${zodToReadableType(value)}`)
			.join('\n')}
		}
		`;

	const systemPrompt = `
			You are a chart style options assistant. You edit only the fields the user mentions.

			DO NOT use any tools.
			
			Here are the editable properties:
			${editableFieldsDoc}
			
			DO NOT return any field unless the user directly references it.
			Your output must be a JSON object with only the requested changes.
			Do not include unchanged fields.
			
			Example:
			User "name the y axis title to Awesome Revenue"
			Output: { "styleOptions": { "yAxis": { "title": "Awesome Revenue", enabled: true } } }

			User: "move legend to top"
			Output: { "styleOptions": { "legend": { "verticalAlign": "top", "align": "center" } } }

			`;

	const parsedResponse = await createStructuredCompletion({
		systemPrompt,
		userPrompt,
		responseFormat: { type: 'json_object' },
	});

	devDebug('toolCall', {
		tool: 'updateChartStyleOptionsHelper',
		phase: 'styleOptionsDelta',
		parsedResponse,
	});

	const { styleOptions: responseStyleOptions = currentStyleOptions } =
		parsedResponse as z.infer<typeof targetSchema>;

	const mergedStyleOptions = deepmerge(
		currentStyleOptions || {},
		responseStyleOptions || {},
	);
	const updatedWidgetProps: ExtendedChartWidgetProps = {
		...chartWidgetProps,
		styleOptions: mergedStyleOptions,
	};

	return updatedWidgetProps;
};

export const updateChartStyleOptions = async (
	{ chartId, userPrompt }: z.infer<typeof updateChartSchema>,
	context: BuildChartContext,
) => {
	devDebug('toolCall', {
		tool: 'updateChartStyleOptions',
		chartId,
		userPrompt,
	});
	const chartWidgetProps = context.retrieveChart?.(chartId);
	if (!chartWidgetProps) {
		throw new Error(`Chart not found`);
	}

	const updatedChartWidgetProps = await updateChartStyleOptionsHelper({
		userPrompt,
		chartWidgetProps,
	});
	context.saveChart?.(chartId, updatedChartWidgetProps);
	return {
		message: 'Chart style options updated successfully',
	};
};
